'use strict';

import { SwipeDetector } from '../../../utils/touch';
import {
    setTransformStyle,
    setTransitionStyle,
    normalizePage,
    getTotalPages,
    getPagedItemWidth,
    setWidthStyle,
} from './utils';

const noop = () => {};

// The transition effect to use when the swipe occurs.
const TRANSITION = 'transform 250ms ease';
// The amount to slow down the dragging effect by when the user reaches the end of the list.
const SLOW_DOWN_FACTOR = 2.5;

/**
 * View-model for carousels that are swiped through on a touch device.
 * @class
 */
class Model {
    constructor({
        itemsPerPage,
        itemCount,
        startingPage = 0,
        onPageChange = noop,
        onStateChange = noop,
    } = {}) {
        this.onChangeCallbacks = [];

        const totalPages = getTotalPages(itemCount, itemsPerPage);

        Object.assign(this, {
            _el: null,
            _itemsPerPage: itemsPerPage,
            _itemCount: itemCount,
            _totalPages: totalPages,
            _onPageChange: onPageChange,
            // Calculate the width of each item.
            _width: getPagedItemWidth(itemsPerPage),
            _swipeDetector: null,
            _onStateChange: (...args) => {
                onStateChange.call(onStateChange, ...args);
                this.onChangeCallbacks.forEach(callback => callback.call(callback, ...args));
            },
            _state: {
                page: normalizePage(startingPage, totalPages),
                // Track the position of the items when dragging through the list.
                offset: 0,
                // Track whether we should use the transition effect during a given render.
                transition: true,
                disabled: false,
            },
        });
    }

    enableSwipe() {
        this._state.disabled = false;
    }

    disableSwipe() {
        this._state.disabled = true;
    }

    /**
     * Pass in the DOM element to manage. This should be a raw DOM element and not a React or jQuery object. The reason
     * the element cannot be passed into the constructor is that initial construction is meant to take place outside of
     * the base carousel but the element is only accessible after construction, once the base carousel has been rendered
     * for the first time. It is possible the "view-model" doesn't need to do anything with the element in which case
     * the `_el` property can be ignored.
     * @param  {Object} el DOM element to manage.
     */
    onMount(el) {
        if (!el) {
            throw new Error('Must pass `el` to carousel model on mount.');
        }

        this._el = el;
        this._afterMount();
    }

    updateItemCount(itemCount, itemsPerPage) {
        this._itemCount = itemCount;
        this._totalPages = getTotalPages(itemCount, itemsPerPage);
    }

    /**
     * Call the state change callback with the current state.
     */
    _updateState() {
        this._onStateChange(this._state);
    }

    /**
     * Custom logic after an element is passed in `onMount`, e.g. adding event listeners on the element.
     */
    _afterMount() {
        // Add swipe handlers.
        this._swipeDetector = new SwipeDetector({
            onPartialSwipe: this._updateOffset.bind(this),
            onSwipeSuccess: this._swipe.bind(this),
            onSwipeFailure: this._resetOffset.bind(this),
            el: this._el,
        });
    }

    /**
     * Do any cleanup necessary to avoid memory leaks, e.g. removing event listeners from the passed in element.
     */
    destroy() {
        this._swipeDetector.destroy();
    }

    /**
     * The `_offset` is used to track the "panning" that happens when the user drags the carousel before the full swipe occurs
     *
     * @returns {object}
     */
    _getTransformValue() {
        const fullContainerWidth = 100; // 100% - swipe carousel uses percentages for animations
        let percentage = -this._width * Math.floor(this._itemsPerPage) * this._state.page;

        // Last page
        if (this._isLastPage() && this._itemsPerPage !== 1) {
            percentage = -this._width * this._itemCount + fullContainerWidth;
        }

        if (this._state.offset !== 0) {
            return `calc(${percentage}% + ${this._state.offset}px)`;
        }

        return `${percentage}%`;
    }

    /**
     * Modify the styles object for the carousel `ul`. This gets called before a re-render of the carousel.
     *
     * @param {Object} style - Style object to mutate for the carousel `el`.
     */
    transformListStyle(style) {
        const transformValue = this._getTransformValue();

        setTransformStyle(style, `translateX(${transformValue})`);

        // When the user is just "panning" we don't want to have a transition effect.
        if (this._state.transition) {
            setTransitionStyle(style, TRANSITION);
        }
    }

    /**
     * Modify the styles object for the carousel item `li`s. This gets called before a re-render of the carousel.
     *
     * @param {Object} style - Style object to mutate for each carousel item `li`.
     * @param {Number} i - The index of the item.
     */
    transformItemStyle(style) {
        setWidthStyle(style, this._width);
    }

    /**
     * Update the position of the carousels in response to "drag/pan" by the user.
     * @param {Array} touchCords Array consisting of the touch coordinates [x, y], passed from the `SwipeDetector`.
     * @param {String} direction The swipe direction.
     */
    _updateOffset(touchCords, direction) {
        // We don't wanna move the carousel when the user is swiping vertically
        if (this._state.disabled || direction === 'up' || direction === 'down') {
            return;
        }

        let xOffset = touchCords[0];

        // If we've reached either end of the list, slow down the dragging effect to indicate to the user that they can't continue.
        if (
            (this._isLastPage() && direction === 'left') ||
            (this._isFirstPage() && direction === 'right')
        ) {
            xOffset = xOffset / SLOW_DOWN_FACTOR;
        }

        this._state.transition = false;
        this._state.offset = xOffset;
        this._updateState();
    }

    /**
     * Call in response to a complete swipe.
     * @param {String} direction The swipe direction.
     */
    _swipe(direction) {
        if (this._state.disabled) {
            return;
        }

        if (direction === 'left') {
            this._nextPage();
        } else if (direction === 'right') {
            this._previousPage();
        }
    }

    /**
     * Unset the "drag" offset and go back to the initial page position.
     */
    _resetOffset() {
        if (this._state.disabled) {
            return;
        }

        this._state.transition = true;
        this._state.offset = 0;

        this._updateState();
    }

    /**
     * Set the new page. Handles bounds issues by going staying on either the first or last page.
     * @param {Number} page The page to go to.
     */
    _setPage(page) {
        page = normalizePage(page, this._totalPages);

        // Only update if the page actually changed.
        if (page !== this._state.page) {
            this._state.transition = true;
            this._state.offset = 0;
            const previous = this._state.page;
            this._state.page = page;
            this._onPageChange(page, previous);
            this._updateState();
        }
    }

    /**
     * Return the current state.
     * @return {[type]} [description]
     */
    getState() {
        return this._state;
    }

    /**
     * Add callbacks when model state changes
     * @param  {Function} callback
     */
    addChangeListener(callback) {
        this.onChangeCallbacks.push(callback);
    }

    /**
     * Remove callback from model state change listener
     * @param  {Function} callback function to remove
     */
    removeChangeListener(callback) {
        this.onChangeCallbacks = this.onChangeCallbacks.filter(cb => cb !== callback);
    }

    /**
     * Go to the next page.
     */
    _nextPage() {
        // If we are already on the last page, just stay there.
        if (this._isLastPage()) {
            this._resetOffset();
            return;
        }

        this._setPage(this._state.page + 1);
    }

    /**
     * Go to the previous page.
     */
    _previousPage() {
        // If we are already on the first page, just stay there.
        if (this._isFirstPage()) {
            this._resetOffset();
            return;
        }

        this._setPage(this._state.page - 1);
    }

    /**
     * Check if we are currently on the first page.
     * @return {Boolean}
     */
    _isFirstPage() {
        return this._state.page === 0;
    }

    /**
     * Check if we are currently on the last page.
     * @return {Boolean}
     */
    _isLastPage() {
        return this._state.page === this._totalPages - 1;
    }
}

export default Model;
